import React, { FC, useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'

import { StandardButton } from 'src/components/atoms'
import {
  SendPasswordMailCheckbox,
  FormItemHorizontal,
  WorksRoleDropdown,
  UserInvitationTextArea,
} from 'src/components/molecules'
import { SelectCustomRoleDropdown } from 'src/components/organisms/customRoles/SelectCustomRoleDropdown'

import { yup, BaseYup, yupResolver } from 'src/customYup'
import { useMasterData, useUser, useToast, useGetProfile, useGA } from 'src/hooks'
import { CreateInternalUserFormEntity } from 'src/types'

const schema: yup.SchemaOf<CreateInternalUserFormEntity> = BaseYup.object({
  roleId: BaseYup.number().required(),
  emails: BaseYup.array().of(BaseYup.string().email('メールアドレスをご確認下さい').required()),
  isSendEmail: BaseYup.boolean().required(),
  customRoleIds: BaseYup.array().of(BaseYup.number().required()),
})

type Props = {
  afterCreate?: () => void
  className?: string
}

export const CreateUser: FC<Props> = ({ afterCreate, className }) => {
  const [isLoading, setIsLoading] = useState(false)

  const { loading: masterDataLoading, masterData } = useMasterData()
  const { createUsers } = useUser()
  const { profile } = useGetProfile()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { sendEvent } = useGA()

  const {
    setValue,
    watch,
    trigger,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateInternalUserFormEntity>({
    resolver: yupResolver(schema),
    defaultValues: {
      // メンバーロールをデフォルトに設定
      roleId:
        masterData?.worksRoles.find(role => role.name === 'member')?.id ??
        masterData?.worksRoles[0].id,
      isSendEmail: false,
    },
  })

  useEffect(() => {
    if (masterDataLoading && masterData?.worksRoles[0]) {
      setValue('roleId', masterData?.worksRoles[0].id)
    }
  }, [masterDataLoading, masterData?.worksRoles, setValue])

  const onSubmit = async (formEntity: CreateInternalUserFormEntity) => {
    setIsLoading(true)

    try {
      const { successes } = await createUsers({ formEntity })
      displayStandardTextToast(
        `${formEntity.emails.length}人中${successes.length}人を追加しました。`
      )

      sendEvent('invite', { user_type: 'internal' })

      afterCreate && afterCreate()
    } catch (error) {
      console.log('e: ', error)
      // TODO: エラー内容に応じて処理を分ける
      displayErrorTextToast('ユーザーの作成に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={className}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="tw-grid tw-gap-s2">
          <FormItemHorizontal title="WORKS役割">
            {!masterDataLoading && masterData && profile && (
              <WorksRoleDropdown
                value={watch('roleId') ?? 1}
                worksRoles={masterData.worksRoles}
                profile={profile}
                selectOption={selectedRoleId => setValue('roleId', selectedRoleId)}
                size="m"
                className="tw-w-[16em]"
              />
            )}
          </FormItemHorizontal>
          <FormItemHorizontal title="カスタムロール">
            <SelectCustomRoleDropdown
              alreadyAssignedCustomRoleIds={watch('customRoleIds') ?? []}
              selectCustomRole={customRole =>
                setValue('customRoleIds', [...(watch('customRoleIds') ?? []), customRole.id])
              }
              removeCustomRole={customRole =>
                setValue(
                  'customRoleIds',
                  (watch('customRoleIds') ?? []).filter(id => id !== customRole.id)
                )
              }
            />
          </FormItemHorizontal>
          <FormItemHorizontal
            title={[
              'メールアドレス',
              <br key="createUserEmailText-2" />,
              <span className="tw-text-standardText-secondary" key="createUserEmailText-3">
                （複数可）
              </span>,
            ]}
          >
            <UserInvitationTextArea
              emails={watch('emails')}
              updateEmails={updatedEmails => setValue('emails', updatedEmails)}
              emailFieldErrors={errors.emails}
              onTrigger={async () => trigger('emails')}
            />
          </FormItemHorizontal>
        </div>

        <SendPasswordMailCheckbox
          type="internal"
          isSendEmail={watch('isSendEmail')}
          updateCheck={updateValue => setValue('isSendEmail', updateValue)}
          className="tw-w-fit tw-mx-auto tw-mt-s3"
        />

        <div className="tw-centering tw-mt-s2">
          <StandardButton
            size="l"
            text="ユーザー追加"
            prependIconPath="send/soft/line"
            isLoading={isLoading}
            disabled={!watch('emails') || watch('emails')?.length === 0 || !!errors.emails}
          />
        </div>
      </form>
    </div>
  )
}
